import React, { useEffect, useState, useCallback, useRef } from "react";
import * as A from "./lib/api";
import { H1, H2, Sub, Card } from "./Pages";
import { toast } from "sonner";
import { Plus, Trash2, Lock, Unlock, HeartPulse, Flower2, Phone, Users2, Globe, Home as HomeIcon, Mail,
  Share2, X, Send, RotateCcw, ShieldCheck, Bell, FileText, UploadCloud, Download, History, KeyRound, ScrollText, Landmark, Network, Paperclip } from "lucide-react";
import MediaUploader from "./components/MediaUploader";
import MediaGallery from "./components/MediaGallery";

const ROLE_OPTS = ["Beneficiary", "Executor", "Trustee", "Attorney", "Accountant", "Financial Advisor", "Family Member", "Other"];

// Reusable sharing panel — add named people/roles who may view a record.
function SharePanel({ shared, onAdd, onRemove }) {
  const [open, setOpen] = useState(false);
  const [p, setP] = useState({ email: "", name: "", role: "Beneficiary" });
  const add = async () => {
    if (!p.email) { toast.error("Email required"); return; }
    await onAdd(p); setP({ email: "", name: "", role: "Beneficiary" });
  };
  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 flex-wrap">
        <button className="btn-outline text-xs flex items-center gap-1" onClick={() => setOpen(!open)} data-testid="share-toggle"><Share2 size={12} /> Shared with {(shared || []).length ? `(${shared.length})` : ""}</button>
        {(shared || []).map(s => (
          <span key={s.email} className="chip chip-green text-xs flex items-center gap-1">
            {s.name || s.email}{s.role ? ` · ${s.role}` : ""}
            <button onClick={() => onRemove(s.email)} data-testid={`unshare-${s.email}`} title="Remove"><X size={10} /></button>
          </span>
        ))}
      </div>
      {open && (
        <div className="grid md:grid-cols-4 gap-2 mt-2 p-3 rounded" style={{ background: "var(--panel, #f7f5f0)" }} data-testid="share-form">
          <input placeholder="Email" value={p.email} onChange={e => setP({ ...p, email: e.target.value })} data-testid="share-email" />
          <input placeholder="Name" value={p.name} onChange={e => setP({ ...p, name: e.target.value })} data-testid="share-name" />
          <select value={p.role} onChange={e => setP({ ...p, role: e.target.value })} data-testid="share-role">{ROLE_OPTS.map(o => <option key={o}>{o}</option>)}</select>
          <button className="btn-primary text-xs" onClick={add} data-testid="share-add">Grant access</button>
        </div>
      )}
    </div>
  );
}

// Generic owner-scoped registry for the Legacy Vault features.
export function SimpleRegistry({ kind, title, sub, icon: Icon, fields, testid, itemLabel = "record", messageMode = false, allowMedia = false }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [mediaBy, setMediaBy] = useState({});

  const load = useCallback(async () => {
    const r = await A.legacy.list(kind); setRows(r.data);
    if (allowMedia) {
      try {
        const m = await A.media.list("legacy");
        const g = {}; m.data.forEach(a => { (g[a.context_id] = g[a.context_id] || []).push(a); });
        setMediaBy(g);
      } catch (e) {}
    }
  }, [kind, allowMedia]);
  useEffect(() => { load(); }, [load]);

  const blank = () => { const o = {}; fields.forEach(f => o[f.k] = f.type === "checkbox" ? false : ""); return o; };
  const save = async () => {
    try {
      const p = { ...editing };
      if (p.id) { const id = p.id; await A.legacy.patch(kind, id, p); }
      else await A.legacy.create(kind, p);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => { if (window.confirm(`Delete this ${itemLabel}?`)) { try { await A.legacy.del(kind, id); load(); } catch (e) { toast.error("Delete failed"); } } };
  const share = async (id, person) => { try { await A.legacy.share(kind, id, person); toast.success("Access granted"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const unshare = async (id, email) => { try { await A.legacy.unshare(kind, id, email); load(); } catch (e) { toast.error("Failed"); } };
  const release = async (id) => { try { await A.legacy.release(id); toast.success("Message released"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const revoke = async (id) => { try { await A.legacy.revoke(id); toast.success("Message re-sealed"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  if (!rows) return <div>Loading…</div>;
  const primary = fields[0].k;

  return (
    <div data-testid={`${testid}-page`}>
      <H1>{title}</H1>
      <Sub>{sub}</Sub>
      <div className="flex justify-between items-center mb-3">
        <H2>{rows.length} {rows.length === 1 ? itemLabel : itemLabel + "s"}</H2>
        <button className="btn-gold flex items-center gap-1" onClick={() => setEditing(blank())} data-testid={`${testid}-new`}><Plus size={14} /> Add {itemLabel}</button>
      </div>

      {editing && (
        <Card className="mb-4" data-testid={`${testid}-form`}>
          <div className="grid md:grid-cols-2 gap-3">
            {fields.map(f => (
              <div key={f.k} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                <label className="label-caps">{f.label}</label>
                {f.type === "textarea" ? <textarea rows={3} value={editing[f.k] || ""} onChange={e => setEditing({ ...editing, [f.k]: e.target.value })} data-testid={`${testid}-field-${f.k}`} />
                  : f.type === "select" ? <select value={editing[f.k] || ""} onChange={e => setEditing({ ...editing, [f.k]: e.target.value })} data-testid={`${testid}-field-${f.k}`}><option value="">—</option>{f.options.map(o => <option key={o}>{o}</option>)}</select>
                  : f.type === "checkbox" ? <label className="flex items-center gap-2 text-sm mt-1"><input type="checkbox" className="w-auto" checked={!!editing[f.k]} onChange={e => setEditing({ ...editing, [f.k]: e.target.checked })} data-testid={`${testid}-field-${f.k}`} /> {f.hint || f.label}</label>
                  : <input type={f.type || "text"} value={editing[f.k] || ""} onChange={e => setEditing({ ...editing, [f.k]: e.target.value })} data-testid={`${testid}-field-${f.k}`} />}
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3"><button className="btn-primary" onClick={save} data-testid={`${testid}-save`}>Save</button><button className="btn-outline" onClick={() => setEditing(null)}>Cancel</button></div>
        </Card>
      )}

      <div className="space-y-2">
        {rows.map(r => (
          <Card key={r.id} data-testid={`${testid}-row-${r.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium flex items-center gap-2" style={{ color: 'var(--primary)' }}>
                  {Icon && <Icon size={15} />}{r[primary] || "Untitled"}
                  {messageMode && (r._locked ? <span className="chip chip-amber text-xs"><Lock size={10} className="inline" /> Sealed</span>
                    : r.released ? <span className="chip chip-green text-xs"><Unlock size={10} className="inline" /> Released</span>
                    : r.Sealed ? <span className="chip chip-amber text-xs"><Lock size={10} className="inline" /> Sealed</span>
                    : <span className="chip chip-green text-xs"><Unlock size={10} className="inline" /> Visible</span>)}
                  {!r._is_owner && <span className="chip text-xs" style={{ background: "#eef", color: "#446" }}>Shared with you</span>}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                  {fields.slice(1).filter(f => f.type !== "checkbox" && r[f.k]).map(f => `${f.label}: ${r[f.k]}`).join(" · ")}
                </div>
                {messageMode && r._locked && <div className="text-xs mt-2 italic" style={{ color: 'var(--muted)' }}>{r.Body}</div>}
                {r._is_owner && <SharePanel shared={r.shared_with} onAdd={(p) => share(r.id, p)} onRemove={(em) => unshare(r.id, em)} />}
                {allowMedia && (
                  <div className="mt-2">
                    {(mediaBy[r.id] || []).length > 0 && <MediaGallery assets={mediaBy[r.id]} onChanged={load} canManage={r._is_owner} compact />}
                    {r._is_owner && <div className="mt-1"><MediaUploader contextType="legacy" contextId={r.id} visibility="Private" label="Attach photo/scan" onUploaded={load} testid={`${testid}-media-${r.id}`} /></div>}
                  </div>
                )}
              </div>
              {r._can_edit && (
                <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                  {messageMode && r.Sealed && !r.released && <button className="btn-outline text-xs flex items-center gap-1" style={{ color: 'var(--gold, #8a6d1a)' }} onClick={() => release(r.id)} data-testid={`${testid}-release-${r.id}`}><Send size={12} /> Release</button>}
                  {messageMode && r.released && <button className="btn-outline text-xs flex items-center gap-1" onClick={() => revoke(r.id)} data-testid={`${testid}-revoke-${r.id}`}><RotateCcw size={12} /> Re-seal</button>}
                  <button className="btn-outline text-xs" onClick={() => setEditing(r)} data-testid={`${testid}-edit-${r.id}`}>Edit</button>
                  <button className="btn-outline text-xs" style={{ color: 'var(--overdue)' }} onClick={() => del(r.id)}><Trash2 size={12} /></button>
                </div>
              )}
            </div>
          </Card>
        ))}
        {rows.length === 0 && <Card><div className="text-sm" style={{ color: 'var(--muted)' }}>Nothing here yet — add your first {itemLabel} above.</div></Card>}
      </div>
    </div>
  );
}

export const PeopleDirectoryPage = () => <SimpleRegistry testid="people" kind="people_directory" itemLabel="person"
  title="Beneficiaries & Executors" icon={Users2}
  sub="Your circle of trust — add each person, assign their role, and note how to reach them. People with an 'Executor' role may help release sealed messages and activate the estate when authorized."
  fields={[{ k: "Name", label: "Full name" }, { k: "Role", label: "Role", type: "select", options: ROLE_OPTS }, { k: "Email", label: "Email", type: "email" }, { k: "Phone", label: "Phone" }, { k: "Relationship", label: "Relationship" }, { k: "AccessNotes", label: "Access / permission notes", type: "textarea" }]} />;

export const DigitalAssetsPage = () => <SimpleRegistry testid="digital" kind="digital_assets" itemLabel="account" allowMedia
  title="Digital Asset Inventory" icon={Globe}
  sub="A private record of online accounts — email, banking, social, subscriptions, domains, wallets, cloud — with login hints, custodians and release conditions. Never store full passwords or private keys; use hints only."
  fields={[{ k: "AccountName", label: "Account / service" }, { k: "Category", label: "Category", type: "select", options: ["Email", "Banking", "Social Media", "Subscription", "Domain Name", "Cloud Storage", "Crypto/Wallet", "Intellectual Property", "Other"] }, { k: "Username", label: "Username / login" }, { k: "LoginHint", label: "Login hint (not the password)" }, { k: "Custodian", label: "Custodian / provider" }, { k: "ReleaseCondition", label: "Release condition for heirs" }, { k: "HeirInstructions", label: "Instructions for heirs", type: "textarea" }]} />;

export const PhysicalAssetsPage = () => <SimpleRegistry testid="physical" kind="physical_assets" itemLabel="asset" allowMedia
  title="Physical Asset Inventory" icon={HomeIcon}
  sub="Real-world assets your family should know about — property, vehicles, valuables, policies, accounts and business interests — with ownership, valuation, liabilities and location."
  fields={[{ k: "AssetName", label: "Asset" }, { k: "Category", label: "Category", type: "select", options: ["Real Property", "Vehicle", "Jewelry / Valuables", "Insurance Policy", "Financial Account", "Business Interest", "Collectible", "Other"] }, { k: "Ownership", label: "Owner / titleholder" }, { k: "EstimatedValue", label: "Estimated value ($)", type: "number" }, { k: "ValuationDate", label: "Valuation date", type: "date" }, { k: "Liabilities", label: "Liabilities / loans against it" }, { k: "TrustEntity", label: "Trust / entity association" }, { k: "Location", label: "Location / where to find it" }, { k: "Details", label: "Details & instructions", type: "textarea" }]} />;

export const LegacyMessagesPage = () => <SimpleRegistry testid="legacy" kind="legacy_messages" itemLabel="message" messageMode
  title="Legacy Messages" icon={Mail}
  sub="Personal notes for the people you love. Share a message with a recipient, then seal it — sealed messages stay invisible to recipients until you or your designated executor release them. You may re-seal (revoke) at any time."
  fields={[{ k: "ForPerson", label: "For (recipient name)" }, { k: "Title", label: "Message title" }, { k: "Body", label: "Your message", type: "textarea" }, { k: "VideoUrl", label: "Video / audio message link (optional)" }, { k: "Sealed", label: "Sealed — hidden until released", type: "checkbox", hint: "Seal this message (released manually by you or your executor)" }]} />;

export const HealthcareDirectivesPage = () => <SimpleRegistry testid="healthcare" kind="healthcare_directives" itemLabel="directive"
  title="Healthcare & End-of-Life Directives" icon={HeartPulse}
  sub="Advance directives, living wills, and powers of attorney — clearly separated from your general documents so the right people can find them quickly when it matters most."
  fields={[{ k: "DocumentType", label: "Document type", type: "select", options: ["Advance Directive", "Living Will", "Healthcare Power of Attorney", "Financial Power of Attorney", "DNR", "Other"] }, { k: "PersonName", label: "For whom" }, { k: "Agent", label: "Named agent / proxy" }, { k: "DateSigned", label: "Date signed", type: "date" }, { k: "Status", label: "Status", type: "select", options: ["Draft", "Signed", "Under Review", "Superseded"] }, { k: "Location", label: "Where the original is kept" }, { k: "Notes", label: "Notes", type: "textarea" }]} />;

export const FinalWishesPage = () => <SimpleRegistry testid="wishes" kind="final_wishes" itemLabel="wish"
  title="Funeral & Final Wishes" icon={Flower2}
  sub="A gentle place to leave your wishes for arrangements, disposition preferences, ceremonies and final requests. These are wishes and instructions — not legally controlling instruments."
  fields={[{ k: "Title", label: "Wish / topic" }, { k: "Category", label: "Category", type: "select", options: ["Service / Ceremony", "Burial / Cremation / Disposition", "Music & Readings", "Guests to Notify", "Charitable Requests", "Personal Message", "Other"] }, { k: "Details", label: "Your wishes", type: "textarea" }]} />;

export const EmergencyContactsPage = () => <SimpleRegistry testid="emergency" kind="emergency_contacts" itemLabel="contact"
  title="Emergency Contact Card" icon={Phone}
  sub="A quick-reference card of the key people your family and executor may need to reach — with role, contact method, availability and the trust/entity they relate to."
  fields={[{ k: "Name", label: "Name" }, { k: "Role", label: "Role", type: "select", options: ["Attorney", "Accountant / CPA", "Financial Advisor", "Insurance Agent", "Doctor", "Clergy", "Trustee", "Executor", "Other"] }, { k: "Firm", label: "Firm / organization" }, { k: "Phone", label: "Phone" }, { k: "Email", label: "Email", type: "email" }, { k: "Availability", label: "Availability notes" }, { k: "TrustEntity", label: "Trust / entity scope" }, { k: "Notes", label: "Notes", type: "textarea" }]} />;

export const FamilyOfficePage = () => <SimpleRegistry testid="familyoffice" kind="family_office_items" itemLabel="item"
  title="Family Office & Household Office" icon={Users2}
  sub="A private coordination hub for the family's affairs — investments, tax notes, philanthropy, advisers, insurance, property, contracts, vendors, budgets, risks, meetings, continuity and succession. This is a coordination hub, not automatic accounting or investment advice."
  fields={[{ k: "Title", label: "Item" }, { k: "Category", label: "Category", type: "select", options: ["Investments", "Tax Planning", "Philanthropy", "Governance", "Advisers", "Insurance", "Property", "Contracts / Vendors", "Budget / Cash Flow", "Risk", "Meetings", "Continuity / Succession", "Other"] }, { k: "Owner", label: "Responsible party" }, { k: "Status", label: "Status" }, { k: "Details", label: "Details & notes", type: "textarea" }]} />;

export const FamilyGovernancePage = () => <SimpleRegistry testid="governance" kind="governance_records" itemLabel="record"
  title="Family Governance" icon={Landmark}
  sub="Light, durable governance for the family: mission and constitution, family-council roles and cadence, meeting minutes, a decision register, policies, and shared values. Governance separates ownership from management and confers no legal authority by itself; collective decisions require collective approval."
  fields={[{ k: "Title", label: "Title" }, { k: "Type", label: "Type", type: "select", options: ["Mission / Constitution", "Council Role", "Meeting Minutes", "Decision Register Entry", "Policy", "Value / Principle", "Charter", "Other"] }, { k: "Date", label: "Date", type: "date" }, { k: "Participants", label: "Participants / roles" }, { k: "Status", label: "Status", type: "select", options: ["Draft", "Adopted", "Under Review", "Superseded"] }, { k: "Details", label: "Details", type: "textarea" }]} />;

export const BusinessSuccessionPage = () => <SimpleRegistry testid="succession" kind="succession_plans" itemLabel="plan"
  title="Business Succession Planning" icon={Network}
  sub="Track succession for each business interest — current owners, designated successors, trigger events, timeline, and valuation basis. These are planning records; legal effect and valuations require professional review (marked accordingly)."
  fields={[{ k: "Entity", label: "Business / entity" }, { k: "CurrentOwners", label: "Current owner(s)" }, { k: "Successor", label: "Designated successor(s)" }, { k: "Trigger", label: "Trigger event", type: "select", options: ["Retirement", "Disability", "Death", "Voluntary transfer", "Deadlock", "Sale", "Other"] }, { k: "Timeline", label: "Timeline / milestones" }, { k: "ValuationBasis", label: "Valuation basis (professional review required)" }, { k: "Readiness", label: "Readiness", type: "select", options: ["Not started", "In progress", "Documented", "Professional Review Required", "Complete"] }, { k: "Notes", label: "Notes", type: "textarea" }]} />;

// ==================== SECURE DOCUMENT VAULT ====================
const CLASSIFICATIONS = ["General", "Confidential", "Restricted", "Legal", "Financial", "Medical"];
const VAULT_CATEGORIES = ["Document", "Trust Instrument", "Will", "Deed / Title", "Statement", "Policy", "Photo / Scan", "Other"];

export function VaultPage() {
  const [docs, setDocs] = useState(null);
  const [label, setLabel] = useState("");
  const [classification, setClassification] = useState("General");
  const [category, setCategory] = useState("Document");
  const [busy, setBusy] = useState(false);
  const [shareFor, setShareFor] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => { const r = await A.vault.list(); setDocs(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (replaces = "") => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f); fd.append("label", label || f.name);
      fd.append("classification", classification); fd.append("category", category);
      if (replaces) fd.append("replaces", replaces);
      await A.vault.upload(fd);
      toast.success(replaces ? "New version uploaded" : "Document uploaded");
      setLabel(""); if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  const download = async (d) => {
    try { const r = await A.vault.downloadBlob(d.id); A.saveBlob(r.data, d.original_filename || d.label); }
    catch (e) { toast.error("Download failed"); }
  };
  const del = async (id) => { if (window.confirm("Delete this document?")) { await A.vault.del(id); load(); } };
  const share = async (id, person) => { try { await A.vault.share(id, person); toast.success("Access granted"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const unshare = async (id, email) => { try { await A.vault.unshare(id, email); load(); } catch (e) { toast.error("Failed"); } };

  if (!docs) return <div>Loading…</div>;
  return (
    <div data-testid="vault-page">
      <H1>Secure Document Vault</H1>
      <Sub>Upload, label, classify and version your important files, then share each one only with the specific people who should see it. Access is enforced on every preview, download and export.</Sub>

      <Card className="mb-4" data-testid="vault-upload-card">
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <div><label className="label-caps">File</label><input type="file" ref={fileRef} data-testid="vault-file" /></div>
          <div><label className="label-caps">Label</label><input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Trust Certificate" data-testid="vault-label" /></div>
          <div><label className="label-caps">Classification</label><select value={classification} onChange={e => setClassification(e.target.value)} data-testid="vault-classification">{CLASSIFICATIONS.map(c => <option key={c}>{c}</option>)}</select></div>
          <div><label className="label-caps">Category</label><select value={category} onChange={e => setCategory(e.target.value)} data-testid="vault-category">{VAULT_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></div>
        </div>
        <button className="btn-primary mt-3 flex items-center gap-1" disabled={busy} onClick={() => upload()} data-testid="vault-upload-btn"><UploadCloud size={14} /> {busy ? "Uploading…" : "Upload document"}</button>
      </Card>

      <div className="space-y-2">
        {docs.map(d => (
          <Card key={d.id} data-testid={`vault-row-${d.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium flex items-center gap-2" style={{ color: 'var(--primary)' }}>
                  <FileText size={15} /> {d.label}
                  <span className="chip chip-amber text-xs">{d.classification}</span>
                  <span className="chip text-xs" style={{ background: "#eef", color: "#446" }}>{d.category}</span>
                  {d.version_count > 1 && <span className="chip text-xs flex items-center gap-1"><History size={10} /> v{d.version_count}</span>}
                  {!d._is_owner && <span className="chip text-xs" style={{ background: "#eef", color: "#446" }}>Shared with you</span>}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{d.original_filename} · {(d.size / 1024).toFixed(1)} KB · owner {d.owner_email}</div>
                {d._is_owner && <SharePanel shared={d.shared_with} onAdd={(p) => share(d.id, p)} onRemove={(em) => unshare(d.id, em)} />}
              </div>
              <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                <button className="btn-outline text-xs flex items-center gap-1" onClick={() => download(d)} data-testid={`vault-download-${d.id}`}><Download size={12} /> Download</button>
                {d._is_owner && <label className="btn-outline text-xs cursor-pointer">New version<input type="file" className="hidden" style={{ display: "none" }} onChange={async (e) => { if (e.target.files[0]) { fileRef.current = { files: e.target.files }; await upload(d.id); } }} /></label>}
                {d._is_owner && <button className="btn-outline text-xs" style={{ color: 'var(--overdue)' }} onClick={() => del(d.id)}><Trash2 size={12} /></button>}
              </div>
            </div>
          </Card>
        ))}
        {docs.length === 0 && <Card><div className="text-sm" style={{ color: 'var(--muted)' }}>No documents yet — upload your first file above.</div></Card>}
      </div>
    </div>
  );
}

// ==================== AUTOMATED REMINDERS ====================
const RECURRENCE = ["None", "Annual", "Quarterly", "Monthly", "Custom"];
export function RemindersPage() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => { const r = await A.reminders.list(); setRows(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  const blank = () => ({ title: "", due_date: "", recurrence: "Annual", notify_email: true, notify_beneficiaries: false, notes: "" });
  const save = async () => {
    try { if (editing.id) await A.reminders.patch(editing.id, editing); else await A.reminders.create(editing);
      toast.success("Saved"); setEditing(null); load(); } catch (e) { toast.error("Save failed"); }
  };
  const del = async (id) => { if (window.confirm("Delete reminder?")) { await A.reminders.del(id); load(); } };
  const run = async () => { try { const r = await A.reminders.run(); toast.success(`Dispatched ${r.data.dispatched} due reminder(s)`); load(); } catch (e) { toast.error("Failed"); } };
  if (!rows) return <div>Loading…</div>;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div data-testid="reminders-page">
      <H1>Automated Reminders</H1>
      <Sub>Set annual or custom review reminders tied to your trusts, assets, documents, designations, policies and compliance items. Notices appear in-app and, once email/SMS keys are configured, are also delivered by email.</Sub>
      <div className="flex justify-between items-center mb-3">
        <H2>{rows.length} reminder{rows.length === 1 ? "" : "s"}</H2>
        <div className="flex gap-2">
          <button className="btn-outline flex items-center gap-1" onClick={run} data-testid="reminders-run"><Bell size={14} /> Send due now</button>
          <button className="btn-gold flex items-center gap-1" onClick={() => setEditing(blank())} data-testid="reminders-new"><Plus size={14} /> Add reminder</button>
        </div>
      </div>
      {editing && (
        <Card className="mb-4" data-testid="reminders-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="label-caps">Title</label><input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} data-testid="reminders-title" /></div>
            <div><label className="label-caps">Due date</label><input type="date" value={editing.due_date} onChange={e => setEditing({ ...editing, due_date: e.target.value })} data-testid="reminders-due" /></div>
            <div><label className="label-caps">Recurrence</label><select value={editing.recurrence} onChange={e => setEditing({ ...editing, recurrence: e.target.value })} data-testid="reminders-recurrence">{RECURRENCE.map(o => <option key={o}>{o}</option>)}</select></div>
            <div className="flex items-end"><label className="flex items-center gap-2 text-sm"><input type="checkbox" className="w-auto" checked={editing.notify_email} onChange={e => setEditing({ ...editing, notify_email: e.target.checked })} /> Notify me by email</label></div>
            <div className="md:col-span-2"><label className="label-caps">Notes</label><textarea rows={2} value={editing.notes} onChange={e => setEditing({ ...editing, notes: e.target.value })} data-testid="reminders-notes" /></div>
          </div>
          <div className="flex gap-2 mt-3"><button className="btn-primary" onClick={save} data-testid="reminders-save">Save</button><button className="btn-outline" onClick={() => setEditing(null)}>Cancel</button></div>
        </Card>
      )}
      <div className="space-y-2">
        {rows.map(r => (
          <Card key={r.id} data-testid={`reminders-row-${r.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div>
                <div className="font-medium flex items-center gap-2" style={{ color: 'var(--primary)' }}>{r.title}
                  {r.due_date <= today && <span className="chip text-xs" style={{ background: "#fdecea", color: "#a3271d" }}>Due</span>}
                  <span className="chip text-xs">{r.recurrence}</span>
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Due {r.due_date}{r.notes ? ` · ${r.notes}` : ""}{r.last_notified ? ` · last sent ${r.last_notified.slice(0, 10)}` : ""}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button className="btn-outline text-xs" onClick={() => setEditing(r)}>Edit</button>
                <button className="btn-outline text-xs" style={{ color: 'var(--overdue)' }} onClick={() => del(r.id)}><Trash2 size={12} /></button>
              </div>
            </div>
          </Card>
        ))}
        {rows.length === 0 && <Card><div className="text-sm" style={{ color: 'var(--muted)' }}>No reminders yet.</div></Card>}
      </div>
    </div>
  );
}

// ==================== TRUST ACTIVATION FLOW ====================
export function ActivationPage() {
  const [state, setState] = useState(null);
  const [pw, setPw] = useState("");
  const [note, setNote] = useState("");
  const [target, setTarget] = useState(null); // {ownerId, action}
  const [preview, setPreview] = useState(null);
  const load = useCallback(async () => { const r = await A.activation.mine(); setState(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  const startActivate = async (ownerId) => {
    try { const r = await A.activation.preview(ownerId); setPreview(r.data); setTarget({ ownerId, action: "activate" }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not preview activation"); }
  };
  const confirm = async () => {
    if (!pw) { toast.error("Confirm your password"); return; }
    try {
      if (target.action === "activate") await A.activation.activate(target.ownerId, pw, note);
      else await A.activation.revert(target.ownerId, pw);
      toast.success(target.action === "activate" ? "Estate activated" : "Activation reverted");
      setTarget(null); setPw(""); setNote(""); setPreview(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!state) return <div>Loading…</div>;
  const mine = state.mine;
  return (
    <div data-testid="activation-page">
      <H1>Trust Activation</H1>
      <Sub>A controlled transition initiated only by the trust owner, a documented designated executor, or the Trustee Administrator. Activation releases authorized sealed messages and notifies relevant parties. It requires re-authentication and is fully audited. Portal activation does not create legal or fiduciary authority.</Sub>

      <Card className="mb-4" data-testid="activation-mine">
        <div className="flex justify-between items-center gap-3">
          <div>
            <div className="font-medium flex items-center gap-2" style={{ color: 'var(--primary)' }}><ShieldCheck size={16} /> Your estate</div>
            <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Status: <b>{mine.status}</b>{mine.activated_at ? ` · activated ${mine.activated_at.slice(0, 10)} by ${mine.activated_by}` : ""}</div>
          </div>
          {mine.status !== "active"
            ? <button className="btn-gold" onClick={() => startActivate(mine.owner_id)} data-testid="activate-mine">Preview & activate</button>
            : <button className="btn-outline" onClick={() => setTarget({ ownerId: mine.owner_id, action: "revert" })} data-testid="revert-mine">Revert activation</button>}
        </div>
      </Card>

      {state.as_executor?.length > 0 && (
        <>
          <H2>Estates you may act on (as executor)</H2>
          <div className="space-y-2">
            {state.as_executor.map(e => (
              <Card key={e.owner_id} data-testid={`activation-exec-${e.owner_id}`}>
                <div className="flex justify-between items-center gap-3">
                  <div><div className="font-medium" style={{ color: 'var(--primary)' }}>{e.owner_email}</div><div className="text-xs" style={{ color: 'var(--muted)' }}>Status: {e.status}</div></div>
                  {e.status !== "active" && <button className="btn-outline" onClick={() => startActivate(e.owner_id)}>Preview & activate</button>}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {target && (
        <Card className="mt-4" data-testid="activation-confirm">
          <div className="font-medium mb-2" style={{ color: 'var(--primary)' }}>{target.action === "activate" ? "Confirm activation" : "Confirm revert"}</div>
          {target.action === "activate" && preview && (
            <div className="mb-3 p-3 rounded" style={{ background: "var(--surface-2)" }} data-testid="activation-preview">
              <div className="label-caps mb-1">Simulation — what will happen</div>
              <div className="text-sm">This will release <b>{preview.would_release_count}</b> sealed message(s).</div>
              {(preview.would_release || []).length > 0 && (
                <ul className="list-disc pl-5 text-xs mt-1" style={{ color: "var(--muted)" }}>
                  {preview.would_release.map((m) => <li key={m.id}>{m.title} → {(m.recipients || []).join(", ") || "no recipients"}</li>)}
                </ul>
              )}
              <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>{preview.note}</div>
            </div>
          )}
          <p className="text-sm mb-3" style={{ color: 'var(--muted)' }}>{target.action === "activate" ? "This will release authorized sealed messages and notify shared recipients. Enter your password to confirm." : "This will re-seal messages released by activation. Enter your password to confirm."}</p>
          {target.action === "activate" && <div className="mb-2"><label className="label-caps">Note (optional)</label><input value={note} onChange={e => setNote(e.target.value)} data-testid="activation-note" /></div>}
          <div className="mb-3"><label className="label-caps">Confirm password</label><input type="password" value={pw} onChange={e => setPw(e.target.value)} data-testid="activation-password" /></div>
          <div className="flex gap-2"><button className="btn-primary flex items-center gap-1" onClick={confirm} data-testid="activation-confirm-btn"><KeyRound size={14} /> Confirm</button><button className="btn-outline" onClick={() => { setTarget(null); setPw(""); setPreview(null); }}>Cancel</button></div>
        </Card>
      )}
    </div>
  );
}

// ==================== AUDIT TRAIL ====================
export function AuditTrailPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => { A.auditTrail(400).then(r => setRows(r.data)); }, []);
  if (!rows) return <div>Loading…</div>;
  return (
    <div data-testid="audit-page">
      <H1>Audit Trail</H1>
      <Sub>A permanent, append-only record of access and actions. You see events on your own records and actions; the Trustee Administrator sees the full trail across the portal.</Sub>
      <Card>
        <table className="w-full text-sm">
          <thead><tr style={{ color: 'var(--muted)' }} className="text-left text-xs uppercase">
            <th className="py-2">When</th><th>Who</th><th>Action</th><th>Record</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t" data-testid={`audit-row-${r.id}`} style={{ borderColor: "#eee" }}>
                <td className="py-2 whitespace-nowrap text-xs" style={{ color: 'var(--muted)' }}>{(r.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                <td className="text-xs">{r.actor}</td>
                <td>{r.action}</td>
                <td className="text-xs" style={{ color: 'var(--muted)' }}>{r.kind ? `${r.kind}${r.record_id ? " · " + r.record_id.slice(0, 8) : ""}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="text-sm" style={{ color: 'var(--muted)' }}>No audit events yet.</div>}
      </Card>
    </div>
  );
}

// ==================== NOTIFICATIONS ====================
export function NotificationsPage() {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => { const r = await A.notifications.list(); setRows(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  const readAll = async () => { await A.notifications.readAll(); load(); };
  if (!rows) return <div>Loading…</div>;
  return (
    <div data-testid="notifications-page">
      <H1>Notifications</H1>
      <Sub>Controlled notices when documents, messages, assets or reminders are shared, released, activated, or due. Notices never expose restricted content.</Sub>
      <div className="flex justify-end mb-3"><button className="btn-outline text-xs" onClick={readAll} data-testid="notif-read-all">Mark all read</button></div>
      <div className="space-y-2">
        {rows.map(n => (
          <Card key={n.id} data-testid={`notif-row-${n.id}`} className={n.read ? "opacity-60" : ""}>
            <div className="flex items-start gap-2">
              <Bell size={15} style={{ color: 'var(--gold, #8a6d1a)', marginTop: 2 }} />
              <div>
                <div className="font-medium" style={{ color: 'var(--primary)' }}>{n.title}{!n.read && <span className="chip chip-amber text-xs ml-2">New</span>}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{n.body} · {(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
              </div>
            </div>
          </Card>
        ))}
        {rows.length === 0 && <Card><div className="text-sm" style={{ color: 'var(--muted)' }}>No notifications yet.</div></Card>}
      </div>
    </div>
  );
}
